import React from "react";
import "./OurServices.css";
import {
  FaCheckCircle,
  FaDatabase,
  FaWallet,
  FaPercent,
  FaUserCircle,
  FaCar,
  FaIndustry,
  FaBus,
  FaMountain,
  FaTruck,
} from "react-icons/fa";
import { motion } from "framer-motion";

const OurServices = () => {
  const services = [
    {
      title: "State of the Art Route Mapper",
      active: true,
    },
    {
      title: "Live Fleet Tracking on Map",
      active: true,
    },
    {
      title: "Historical Performance Reports",
      active: true,
    },
    {
      title: "Detailed Event Logs for Analysis",
      active: true,
    },
    {
      title: "Variety of Email, Popup Alerts etc",
      active: true,
    },
    {
      title: "Maintenance Module for Market Fleet Owners",
      active: true,
    },
  ];

  const quickAccess = [
    "Quick access to Support Tickets",
    "Access to All features on Mobile Platform too",
    "User Friendly Interface with Easy to use Features",
  ];

  const mobileFeatures = [
    "Fleet Dashboard - Mobile app for Management use",
    "Live Tracking on Map",
    "Wide variety of Mobile Notifications",
    "Consignment Details",
    "Distance Reports Route Map",
    "Nearby Vehicles Feature",
    "Event Reports",
    "Direct Driver Calls",
    "And Many others...",
  ];

  const erpIntegrationData = {
    dataTypes: [
      "Periodic / Trigger Based Data Sharing",
      "Pull & Push Integration",
      "Driver Information",
      "Trip Data",
      "Consignee Data",
      "Invoice / Product Data",
    ],
    capabilities: [
      {
        title: "Receiving and Managing",
        value: "~5000 unique entries/sec",
      },
      {
        title: "Push notifications",
        value: "~1.2 Million / day",
      },
    ],
    integrationMethods: [
      "Write data onto secure URL – Client's server is not exposed to us",
      "Pull data through API – Client pulls data from our server",
    ],
  };

  return (
    <div className="services-page">
      <motion.section
        className="services-heading-section"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="services-heading-content">
          <motion.h1
            className="services-main-title"
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            Our Services
          </motion.h1>
          <motion.p
            className="services-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Enhancing your fleet with advanced technology and all-encompassing
            management solutions.
          </motion.p>
        </div>
      </motion.section>

      {/* Hero Section with Animation */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="animate-slide-up">Logistics & Fleet Automation</h1>
          <p className="animate-fade-in">
            Transforming fleet operations with cutting-edge technology
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">98%</span>
              <span className="stat-label">Accuracy Rate</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">5000+</span>
              <span className="stat-label">Active Users</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Timeline Section */}
      <section className="timeline-section">
        <div className="timeline-header">
          <h2>Our Key Services</h2>
          <p>Comprehensive fleet management solutions for your business</p>
        </div>

        <div className="timeline-items">
          {services.map((service, index) => (
            <div
              key={index}
              className="timeline-item"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3>{service.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Access Features */}
      <section className="quick-access-section">
        <div className="quick-access-header">
          <h2>Quick Access Features</h2>
          <p>Everything you need, right at your fingertips</p>
        </div>

        <div className="quick-access-cards">
          {quickAccess.map((feature, index) => (
            <div key={index} className="quick-access-card">
              <div className="check-icon">
                <FaCheckCircle />
              </div>
              <p className="feature-text">{feature}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ERP Integration Section */}
      <section className="erp-section">
        <div className="section-header">
          <h2>ERP Integration</h2>
          <p>Seamless data integration for informed decision making</p>
        </div>

        <div className="data-grid">
          {erpIntegrationData.dataTypes.map((type, index) => (
            <div key={index} className="data-card">
              <div className="data-icon">
                <FaDatabase />
              </div>
              <h3>{type}</h3>
            </div>
          ))}
        </div>

        <div className="integration-details">
          <div className="volume-info">
            <h3>Transaction Volumes</h3>
            {erpIntegrationData.capabilities.map((cap, index) => (
              <div key={index} className="volume-item">
                <span className="volume-title">{cap.title}</span>
                <span className="volume-value">{cap.value}</span>
              </div>
            ))}
          </div>

          <div className="methods-info">
            <h3>Integration Methods</h3>
            {erpIntegrationData.integrationMethods.map((method, index) => (
              <div key={index} className="method-item">
                <span className="method-bullet"></span>
                <span className="method-text">{method}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Application Section */}
      <section className="mobile-app-section">
        <motion.div
          className="mobile-app-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mobile-app-content">
            <motion.h2
              className="mobile-app-title"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Mobile Application
            </motion.h2>

            <div className="mobile-features-container">
              <div className="mobile-features-list">
                {mobileFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="mobile-feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 10 }}
                  >
                    <div className="feature-icon">
                      <FaCheckCircle />
                    </div>
                    <p>{feature}</p>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="mobile-app-highlight"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="highlight-content">
                  <h3>Comprehensive Mobile Application</h3>
                  <p>Access to your fleet on the Move!</p>
                  <div className="highlight-badge">
                    Market's Leading Tracking Mobile App
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* New FASTag Section */}
      <section className="fastag-section">
        <motion.div
          className="fastag-hero"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="fastag-hero-content">
            <h1>We Provide FASTag With Unmatched Service Support</h1>
            <div className="fastag-tagline">
              <span>#GoCashLess</span>
            </div>
          </div>
          <div className="fastag-logo-container">
            <img
              src="/images/fastTag.jpg"
              alt="FASTag"
              className="fastag-logo"
            />
          </div>
        </motion.div>

        <div className="fastag-features-grid">
          {[
            {
              icon: <FaWallet />,
              title: "Know your remaining balance",
              description:
                "Check your FASTag balance instantly through our secure platform",
            },
            {
              icon: <FaPercent />,
              title: "Discount meter",
              description:
                "Track and utilize available discounts on toll payments",
            },
            {
              icon: <FaUserCircle />,
              title: "Account section",
              description:
                "Manage your FASTag account details and transactions easily",
            },
            {
              icon: <FaCar />,
              title: "Vehicle KYC",
              description: "Seamless vehicle verification and KYC management",
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              className="fastag-feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon-wrapper">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="business-status-section">
        <motion.div
          className="business-status-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="status-header">
            <h2>Current Business Status</h2>
            <motion.div
              className="vehicle-count"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
            >
              <span className="count-label">Total Vehicles Catered So Far</span>
              <span className="count-number">12k+</span>
            </motion.div>
          </div>

          <div className="major-projects">
            <h3>Major Projects Undertaken</h3>
            <div className="projects-grid">
              {[
                {
                  title: "NTPC (Through Thriveni -Sainik), Hazaribagh",
                  icon: <FaIndustry />,
                },
                {
                  title: "Odisha State Road Transport Corporation",
                  subtitle: "(Through our Channel Partner)",
                  icon: <FaBus />,
                },
                {
                  title: "Mahakhanij (Maharashtra Mining Department)",
                  icon: <FaMountain />,
                },
                {
                  title:
                    "Bihar State Milk Cooperative Federation Ltd (COMFED)Sudha Group",
                  icon: <FaIndustry />,
                },
                {
                  title: "Sambalpur Truck Association",
                  icon: <FaTruck />,
                },
              ].map((project, index) => (
                <motion.div
                  key={index}
                  className="project-card"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="project-icon">{project.icon}</div>
                  <div className="project-info">
                    <h4>{project.title}</h4>
                    {project.subtitle && <p>{project.subtitle}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      <section className="industries-section">
        <motion.div
          className="industries-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="industries-title">Major Industries We Cater</h2>
          <motion.div
            className="industries-banner"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/images/industries-logo.png"
              alt="Industries We Cater"
              className="industries-banner-image"
            />
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default OurServices;
